import { PayloadAction } from "@reduxjs/toolkit";
import {
	ContextAny,
	ContextMap,
	InteropRaiseIntent,
	InteropOpenRequest,
	AuthenticationToken,
} from "../../clients/Interop/types";
import {
	OpenError,
	ContextType,
	DisplayMetadata,
	AppIntent,
	IntentMetadata,
	IntentResolution,
	ResolveError,
} from "../../typedefs/FDC3";
import { ActionId } from "../../common/redux/types";
import { WindowDescriptor, SpawnParams, ConnectId } from "../window/types";

// Defines the cleanup frequency in the resolver -- cleanup sends heartbeats, checks for dead messages, etc.
export const CLEANUP_INTERVAL = 10000;

/**
 * This type is used internally by the broadcast action. It can
 * handle either an intent or a context.
 *
 * It supports necessary internal features that are not possible
 * through the desktop agent.
 */
export type InteropBroadcastInternal = {
	type: "broadcast" | "raiseIntent";
	payload: {
		context?: ContextAny;
		intent?: string;
	};
	meta: {
		uuid: string;
		channel?: string;
		// Direct to a specific known uuid. SelectConnect rules will still be applied.
		// Any directed broadcast will be queued if that uuid has not yet registered a listener for this message type.
		to?: string;
		// Don't apply SelectConnect rules. For instance when coming from a SelectConnect module itself.
		noSelectConnect?: boolean;
		// When set to false, the sender's current context will not be updated. This is used by `open` (and maybe `raiseContext`).
		setSenderContext?: boolean;
		pendingActionId?: ActionId;
		// true if resolver originates the broadcast to specific client for initial join or listen
		autoBroadcast?: boolean;
		// if set, contains either "*" or context type; when set, then the FDC3 client should deliver the message only to the specified subscriber.
		// Note: autoBroadcasts without targetSubscriberId would cause some listeners/handlers to be incorrectly invoked (because the context handled by the wildcard listener can overlap with other listeners)
		targetSubscriberId?: string;
	};
};

export type RaiseIntentActionResult = {
	type: InteropRaiseIntent["type"];
	result?: IntentResolution;
	error?: ResolveError;
};

export type OpenActionResult = {
	type: InteropOpenRequest["type"];
	result?: {};
	error?: OpenError;
};

export type InteropMessageWithActonId<T> = {
	fsa: T;
	actionId: ActionId;
};

export type SelectConnectAppList = {
	name: string;
};

export type AutoAssociateRule = {
	allChildren?: boolean;
	selectChildren?: string[];
	usingConnectId?: string;
};

export type SelectConnectRule = {
	cc?: any;
	fwd?: any;
	to?: SelectConnectAppList[];
	from?: SelectConnectAppList[];
	authorize?: SelectConnectAppList[];
	contextType?: string;
	intentType?: string;
	autoAssociate?: AutoAssociateRule;
};

// Interop related configuration for an app. This is in the appd manifest (i.e. component config)
// manifest.foreign.interop
export type InteropConfig = {
	// Intents that this app can handle (alternatively this can be loaded from AppD definition)
	intents?: [
		{
			name: string;
			displayName: string;
			contexts?: [string];
		}
	];
	channels?: {
		// Only this app can listen on the system channels that it specifies here.
		// Any app can broadcast to this channel (SelectConnect rules can protect it of course)
		// Example: "#Symphony" : true
		system?: Record<string, true>;
		// Private channels are only accessible through the API when they are in this section of an app's config
		// Example: "BankOfAmerica" : true
		private?: Record<string, true>;
	};
	// Rules for connecting context broadcasts
	selectConnect?: SelectConnectRule[];
	// True if the linker is in use. If this value isn't explicitly set in config then it will be determined
	// automatically based on the values of isAnApp and foreign?.components?.["Window Manager"]?.showLinker
	useLinker?: boolean;
};

/**
 * The cross-section of Finsemble's config (AppD manifest) that is specific
 * to interop.
 */
export interface AppConfig {
	interop?: InteropConfig;
	signatureKey?: JsonWebKey;
	signatureKeyURL?: string;
}

/**
 * OutgoingMessage's are sent back to subscribing FDC3 clients as the result of a broadcast or intent.
 */
export type OutgoingMessage = {
	uuid: string;
	channel?: string;
	context?: ContextAny;
	intent?: string;
	pendingActionId?: ActionId;
	routerChannel: string;
	// if set, contains either "*" or context type; when set, then the FDC3 client should deliver the message only to the specified subscriber.
	targetSubscriberId?: string;
};

/**
 * OutgoingDispatchMessage's trigger redux actions, based on SelectConnect rules resulting from a broadcast.
 *
 * uuid is either the uuid of the broadcaster or the destination depending on whether the triggered rule
 * was broadcast or destination side (if both then there will be two outgoing messages)
 */
export type OutgoingDispatchMessage = {
	fsa: InteropBroadcastInternal;
	uuid: string;
} & IntentOrContextType;

/**
 * OutgoingSelectConnectChannelMessage's are sent to registered SelectConnect channels from remote apps based on results from a broadcast that target those channels.
 *
 * uuid is either the uuid of the broadcaster or the destination depending on whether the triggered rule
 * was broadcast or destination side (if both then there will be two outgoing messages)
 */
export type OutgoingSelectConnectChannelMessage = {
	routerChannel: string;
	fsa: InteropBroadcastInternal;
	uuid: string;
} & IntentOrContextType;

export type IntentOrContextType = {
	intent?: string;
	contextType?: string;
	context?: ContextAny;
};

export type ClientDestination = {
	type: "client";
	uuid: string;
	channel?: string;
	routerChannel: string;
} & IntentOrContextType;

/**
 * uuid is either the uuid of the broadcaster or the destination depending on whether the triggered rule
 * was broadcast or destination side (if both then there will be two outgoing messages)
 */
export type ConnectDestination = {
	type: "selectConnect";
	name: string;
	uuid: string;
} & IntentOrContextType;

export type Destination = ClientDestination | ConnectDestination;

export type RegisterSelectConnectChannel = {
	name: string;
} & (
	| {
			dispatchAction: string;
	  }
	| {
			routerChannel: string;
	  }
);

/**
 * See finsemble.scheme.json for full config
 */
export type FinsembleComponentConfig = {
	bootParams?: Record<string, any>;
	signatureKey?: Record<string, any>;
	window?: SpawnParams;
	foreign?: Record<string, any>;
	isAnApp?: boolean;
	interop?: {
		useLinker?: boolean;
		autoConnect?: boolean;
		selectConnect?: SelectConnectRule[];
	};
	component?: Record<string, any>;
	childWindowOptions?: Record<string, any>;
	custom?: Record<string, any>;
};

export type AppDIcon = {
	src: string;
	sizes?: string;
};

export type AppDefinition = {
	appId: string;
	name: string;
	type?: string;
	title?: string;
	description?: string;
	manifest?: FinsembleComponentConfig & {
		[index: string]: any;
	};
	manifestType?: string;
	version?: string;
	releaseNotes?: string;
	tooltip?: string;
	images?: object[];
	tags?: string[];
	contactEmail?: string;
	supportEmail?: string;
	publisher?: string;
	icons?: AppDIcon[];
	intents?: {
		name: string;
		displayName: string;
		contexts?: string[];
		appListenerAdded?: boolean;
	}[];
	firstRegistrationTimestamp?: number;
	verified?: boolean;
};

export type InteropClient = {
	readiness: "initializing" | "ready";
	// Unimplemented. We may add this back in the future.
	heartbeatTime: number;
	authenticated: boolean;
	// the router transport the register message originated from; interopService will only accept future messages from this client on this transport to prevent spoofing
	authenticatedTransport: object;
	uuid: string;
	windowName: string;
	appName: string;
	appId: string;
	// the query handler for this client
	routerChannel: string;
	// Contains app config for this client
	config?: AppConfig;
	// Channels that this client belongs to. Could be "global".
	channels: string[];
	connectId?: ConnectId;

	// Channels that the client has "joined". Broadcasts for any joined channel will be sent to handlers that have not specified a specific channel.
	// In other words, this is how to implement addContextListener(contextType, "*")
	joinedChannels: string[];
	// Used only for esoteric calls to getCurrentContext without any context specified
	mostRecentContext?: ContextAny;
	// All subscriptions for this client
	subscriptions: {
		// Global subscriptions are triggered for any joined channel, or "global" if no joined channels
		globals: {
			contexts: {
				// contextType of "*" indicates any context type
				[contextType: string]: CurrentContext;
			};
			intents: {
				// Each intent handler can optionally filter for any number of specific context types.
				// true indicates not to filter by context type
				[intent: string]: ContextType | true;
			};
		};
		// Subscriptions to specific channels, from channel.addContextListener and channel.addIntentListener
		channels: {
			[channel: string]: {
				contexts: {
					// contextType of "*" indicates any context type
					[contextType: string]: CurrentContext;
				};
				intents: {
					// Each intent handler can optionally filter for any number of specific context types.
					// true indicates not to filter by context type
					[intent: string]: ContextType | true;
				};
			};
		};
	};
};

export type LoopDetectionEntry = {
	timestamp: number;
	count: number;
	loopNotificationIssued: boolean;
	timerId?: ReturnType<typeof setTimeout>;
};

export type CurrentContext = {
	context: ContextAny;
	meta: {
		timestamp: number;
		targetSubscriberId?: string;
	};
};

export const EmptyCurrentContext = {
	context: {
		type: "",
	},
	meta: {
		timestamp: 0,
		targetSubscriberId: undefined,
	},
};

export type LinkerClient = {
	connectId?: ConnectId | undefined;
	spawnedFromWorkspace?: boolean;
	joinedChannels: string[];
};

export type LinkerChannel = {
	name: string;
	color: string;
	cssClassName: string;
	label: string;
};

export type InteropRegistrationData = {
	authenticationToken: AuthenticationToken;
	windowDescriptor: WindowDescriptor;
};

// Data model for FDC3 Interop service
export type InteropStore = {
	// linker related state - shared via remote redux to LinkerGroups and Linker
	linker: {
		clients: {
			[windowName: string]: LinkerClient;
		};
		allChannels: LinkerChannel[];
		connectIdToJoinedChannel: Record<string, string>;
		activeWindowName?: string;
	};
	workspace: {
		persistedData: {
			[windowName: string]: Record<string, ContextAny>;
		};
	};
	resolver: {
		// For dynamic authentication, the AuthenticationToken in the register message must be in this list, which is built from tokens the window service's launcher allocates.
		pendingInteropRegistrationData: WindowDescriptor[];
		// map of windowName to actionId, for opens without an initial context
		pendingOpenActionIds: {
			[windowName: string]: ActionId;
		};
		// Configuration for the Interop Service itself
		config: {
			serverUUID: string;
			registeredUIResolver: string | null;
		};
		// Contains references to all of the connected clients
		// Clients are added when they connect
		// Clients are removed when they disconnect or when the window closes
		clients: {
			[uuid: string]: InteropClient;
		};
		selectConnectChannels: {
			[name: string]:
				| {
						name: string;
						dispatchAction: string;
				  }
				| {
						name: string;
						routerChannel: string;
				  };
		};
		// Contains meta information about system channels
		channelMeta: {
			[id: string]: {
				type: string;
				displayMetaData?: DisplayMetadata;
			};
		};
		lastChannelState: {
			[channel: string]: { context: ContextAny; senderUuid: string };
		};
		lastChannelStateByType: {
			[channel: string]: ContextMap;
		};
		// Contains information about apps
		apps: {
			definitions: {
				[appName: string]: AppDefinition;
			};
			intentResolutionInProgress: boolean;
			intentResolutionCurrentActionId: any;
		};
		// Contains information about context types that are defined statically in config
		contextTypes: {
			[contextType: string]: {
				// Apps that can handle the context type (future: currently not specified in AppD config)
				launchableHandlers: {
					[appId: string]: true;
				};
				// Powers findIntentsByContextType
				intents: {
					launchable: AppIntent[];
					live: AppIntent[];
				};
			};
		};
		// Contains information about intents used to drive raiseIntent and raiseIntentForContext
		intents: {
			[intent: string]: {
				info: IntentMetadata;
				// These are all launchable apps that can handle the intent
				launchableHandlers: {
					// Each app can optionally specify any number of specific context types (from AppD config)
					// Keyed on AppId (because it's the most specific)
					[key: string]: ContextType[] | null;
				};
			};
		};
		// Contains contexts or intents that are queued for delivery. Any broadcasts to a specific uuid
		// before that uuid has registered a context handler will be queued. This can occur
		// from calls to open() or from modules that trigger broadcasts based on "clientAdded"
		// actions (e.g. #Workspace)
		queue: {
			[uuidOrWindowName: string]: {
				pendingMessage: InteropBroadcastInternal;
				timestamp: number;
				subscriberIsReady?: boolean;
			}[];
		};
		loopDetectionMap: {
			[uuid: string]: LoopDetectionEntry;
		};
		addIntentListenerMaxInitializationTimeout: number;
	};
	// Testing section. This is only used by unit tests
	root: {
		isVisible: boolean;
		// When turned off from the UI, heartbeat actions will be stifled for easier debugging
		heartbeats: boolean;
		testing: {
			unitTestingEnabled: boolean;
			// Contains a representation of the messages to be sent back to clients
			messagesToSend: OutgoingMessage[];
			// Contains all the dispatches after SelectConnect rules
			dispatchesToSend: PayloadAction<OutgoingDispatchMessage>[];
			// Contains all the messages to remote SelectConnect channels after SelectConnect rules
			selectConnectChannelMessagesToSend: OutgoingSelectConnectChannelMessage[];
			// Contains all the destinations before the SelectConnect rules are run
			destinationsBeforeSelectConnect: Destination[];
			// Contains the triggered rules for this SelectConnect cycle
			triggeredRules: {
				rulePhase: string;
				result: string;
				rule: any;
				uuid: string;
				destination: Destination;
			}[];
			// Set with any runtime errors to display
			error: string;
		};
	};
	"@@cmds"?: any[];
};

export type ResolverStore = InteropStore["resolver"];
export type ResolverApps = InteropStore["resolver"]["apps"];
export type ResolverClients = InteropStore["resolver"]["clients"];
export type ResolverAppsDefinition = InteropStore["resolver"]["apps"]["definitions"];
export type ResolverIntentsData = InteropStore["resolver"]["intents"];
export type ResolverIntent = InteropStore["resolver"]["intents"]["intent"];
export type ResolverChannelMeta = InteropStore["resolver"]["channelMeta"];
export type LinkerStore = InteropStore["linker"];
export type RootStore = InteropStore["root"];
export type WorkspaceStore = InteropStore["workspace"];
export type SelectConnectChannel = ResolverStore["selectConnectChannels"][""];
export type LoopDetectionMap = InteropStore["resolver"]["loopDetectionMap"];

export type ClientAdded = {
	uuid: string;
	config: AppConfig;
	windowName: string;
	appId: string;
	connectId?: ConnectId;
	spawnedFromWorkspace?: boolean;
};

export type ClientRemoved = {
	uuid: string;
	windowName: string;
};

export type DIAM = {
	appId?: string;
	name?: string;
	domain?: string;
	executable?: string;
};

export type RegistrationResult = {
	config: AppConfig;
	authenticated: boolean;
	appId: string;
	windowName: string;
	uuid: string;
	newPendingInteropRegistrationData?: InteropRegistrationData[];
	connectId?: ConnectId;
	spawnedFromWorkspace?: boolean;
	actionId: ActionId;
};
